<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import CheckInput from "../../../../components/ui/form/CheckInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";
import SearchSelectMultiItems from "../../../../components/ui/form/SearchSelectMultiItems.vue";
import { ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const trackedByList = [
  { id: "Quantity", name: "Quantity" },
  { id: "Service", name: "Service" },
  { id: "Serial", name: "Serial" },
  { id: "Batch", name: "Batch" },
];

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  photo: props.data?.photo || "",
  icon: props.data?.icon || "",
  code: props.data?.code || "",
  tracked_by: props.data?.tracked_by || "Quantity",
  name: props.data?.name || props.listCreateName || "",
  tax_details_id: props.data?.tax_details_id || "",
  income_accounts_id: props.data?.income_accounts_id || "",
  expense_accounts_id: props.data?.expense_accounts_id || "",
  inventory_uom_id: props.data?.inventory_uom_id || "",
  item_groups:
    props.data?.item_groups?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  outlets:
    props.data?.outlets?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  item_categories:
    props.data?.item_categories?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  allow_pre_order: props.data?.allow_pre_order || false,
  is_expiry: props.data?.is_expiry || false,
  has_variants: props.data?.has_variants || false,
  variants:
    props.data?.variants?.split(",").reduce((a: any, n: any) => {
      a.push(parseInt(n));
      return a;
    }, []) || "",
  inventory_uom_qty: props.data?.inventory_uom_qty || "",
  purchase_uom_id: props.data?.purchase_uom_id || "",
  purchase_uom_qty: props.data?.purchase_uom_qty || "",
  sales_uom_id: props.data?.sales_uom_id || "",
  sales_uom_qty: props.data?.sales_uom_qty || "",
  active: props.data?.active || true,
  uid: props.data?.uid || "",
});

setFieldRules("code", [required("Code is required")]);
setFieldRules("name", [required("Name is required")]);
setFieldRules("tax_details_id", [required("Tax Definition is required")]);
setFieldRules("income_accounts_id", [required("Income Account is required")]);
setFieldRules("expense_accounts_id", [required("Expense Account is required")]);
setFieldRules("inventory_uom_id", [
  required("Inventory Unit of Measure is required"),
]);
setFieldRules("inventory_uom_qty", [
  required("Inventory Measure Quantity is required"),
]);
setFieldRules("purchase_uom_id", [
  required("Purchase Unit of Measure is required"),
]);
setFieldRules("purchase_uom_qty", [
  required("Purchase Measure Quantity is required"),
]);
setFieldRules("sales_uom_id", [required("Sales Unit of Measure is required")]);
setFieldRules("sales_uom_qty", [
  required("Sales Measure Quantity is required"),
]);
setFieldRules("tracked_by", [required("Item Tracking Type is required")]);
setFieldRules("tax_details_id", [required("Tax Definition is required")]);
/*setFieldRules("item_groups", [required("Select at least 1 Item Group")]);
setFieldRules("item_categories", [
  required("Select at least 1 Item Category required"),
]);*/

const handleSubmit = async (close: boolean, newRecord: boolean) => {
  if (validateForm()) {
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        resetForm();
      } else {
        setFormData(response.data);
        formData.value.item_groups =
          response.data.item_groups?.split(",").reduce((a: any, n: any) => {
            a.push(parseInt(n));
            return a;
          }, []) || "";
        formData.value.outlets =
          response.data.outlets?.split(",").reduce((a: any, n: any) => {
            a.push(parseInt(n));
            return a;
          }, []) || "";
        formData.value.item_categories =
          response.data.item_categories?.split(",").reduce((a: any, n: any) => {
            a.push(parseInt(n));
            return a;
          }, []) || "";
        formData.value.variants =
          response.data.variants?.split(",").reduce((a: any, n: any) => {
            a.push(parseInt(n));
            return a;
          }, []) || "";
      }
    }
  }
};

const handleTrackedBy = () => {
  if (formData.value.tracked_by == "Quantity")
    fields.value.is_expiry.value = false;
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  if (props.mode == 'create') {
      resetForm()
    } else if (props.data) {
      setFormData(props.data)
    }
})


</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
  >
    <template #form>
      <div class="grid md:grid-cols-2 gap-6">
        <div id="document-section">
          <FileUpload
            accept=".pdf,.png,.jpg,.jpeg,.txt,.xls,.xlsx,.dox,.docx,.html,.xml"
            label="Icon Image"
            :max-size="5"
            :multiple="false"
            :readonly="isReadOnly"
            size="mini"
            :image="true"
            path="item-categotries"
            v-model="fields.photo.value"
          />
        </div>
        <TextInput
          v-model="fields.icon.value"
          label="Icon"
          placeholder="Enter Icon name"
          :readonly="isReadOnly"
          :error="fields.icon.touched ? fields.icon.error : null"
          @blur="fields.icon.touched = true"
        />
        <TextInput
          v-model="fields.code.value"
          label="Code"
          placeholder="Enter Code"
          :readonly="isReadOnly"
          :error="fields.code.touched ? fields.code.error : null"
          @blur="fields.code.touched = true"
        />

        <TextInput
          v-model="fields.name.value"
          label="Name"
          placeholder="Enter Name"
          :readonly="isReadOnly"
          :error="fields.name.touched ? fields.name.error : null"
          @blur="fields.name.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.item_groups.value"
          end-point="inventory/item-management/item-groups"
          label="Item Groups"
          :readonly="isReadOnly"
          :error="fields.item_groups.touched ? fields.item_groups.error : null"
          @blur="fields.item_groups.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.outlets.value"
          end-point="system/company/outlets"
          label="Outlets"
          :readonly="isReadOnly"
          :error="fields.outlets.touched ? fields.outlets.error : null"
          @blur="fields.outlets.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.item_categories.value"
          end-point="inventory/item-management/item-categories"
          label="Item Categories"
          :readonly="isReadOnly"
          :error="
            fields.item_categories.touched ? fields.item_categories.error : null
          "
          @blur="fields.item_categories.touched = true"
        />

        <SearchSelect
          v-model="fields.tracked_by.value"
          label="Inventory Tracked By"
          :options="trackedByList"
          :readonly="isReadOnly"
          :error="fields.tracked_by.touched ? fields.tracked_by.error : null"
          @blur="fields.tracked_by.touched = true"
          @select="handleTrackedBy"
        />

        <CheckInput
          v-model="fields.is_expiry.value"
          :readonly="isReadOnly"
          label="Manage Expiry"
          :error="fields.is_expiry.touched ? fields.is_expiry.error : null"
          @blur="fields.is_expiry.touched = true"
        />

        <CheckInput
          v-model="fields.allow_pre_order.value"
          :readonly="isReadOnly"
          label="Allow Pre Order"
          :error="
            fields.allow_pre_order.touched ? fields.allow_pre_order.error : null
          "
          @blur="fields.allow_pre_order.touched = true"
        />

        <CheckInput
          v-model="fields.has_variants.value"
          :readonly="isReadOnly"
          label="Has Variants"
          :error="
            fields.has_variants.touched ? fields.has_variants.error : null
          "
          @blur="fields.has_variants.touched = true"
        />

        <SearchSelectMultiItems
          v-model="fields.variants.value"
          end-point="inventory/item-management/variants"
          label="Variants"
          :readonly="isReadOnly"
          :error="fields.variants.touched ? fields.variants.error : null"
          @blur="fields.variants.touched = true"
        />

        <SearchSelect
          v-model="fields.inventory_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Inventory Unit of Measure"
          :readonly="isReadOnly"
          :error="
            fields.inventory_uom_id.touched
              ? fields.inventory_uom_id.error
              : null
          "
          @blur="fields.inventory_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.inventory_uom_qty.value"
          label="Inventory UOM Qty"
          :readonly="isReadOnly"
          :error="
            fields.inventory_uom_qty.touched
              ? fields.inventory_uom_qty.error
              : null
          "
          @blur="fields.inventory_uom_qty.touched = true"
        />

        <SearchSelect
          v-model="fields.purchase_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Purchase Unit of Measure"
          :readonly="isReadOnly"
          :error="
            fields.purchase_uom_id.touched ? fields.purchase_uom_id.error : null
          "
          @blur="fields.purchase_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.purchase_uom_qty.value"
          label="Purchase UOM Qty"
          :readonly="isReadOnly"
          :error="
            fields.purchase_uom_qty.touched
              ? fields.purchase_uom_qty.error
              : null
          "
          @blur="fields.purchase_uom_qty.touched = true"
        />

        <SearchSelect
          v-model="fields.sales_uom_id.value"
          end-point="inventory/item-management/unit-of-measures"
          label="Sales Unit of Measure"
          :readonly="isReadOnly"
          :error="
            fields.sales_uom_id.touched ? fields.sales_uom_id.error : null
          "
          @blur="fields.sales_uom_id.touched = true"
        />

        <AmountInput
          v-model="fields.sales_uom_qty.value"
          label="Sales UOM Qty"
          :readonly="isReadOnly"
          :error="
            fields.sales_uom_qty.touched ? fields.sales_uom_qty.error : null
          "
          @blur="fields.sales_uom_qty.touched = true"
        />

        <SearchSelect
          v-model="fields.tax_details_id.value"
          label="Tax Definition"
          end-point="finance/master-data/tax-definitions"
          :readonly="isReadOnly"
          :allowNewRecord="true"
          :error="
            fields.tax_details_id.touched ? fields.tax_details_id.error : null
          "
          @blur="fields.tax_details_id.touched = true"
        />

        <SearchSelect
          v-model="fields.income_accounts_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Income Account"
          local-storage="accounts"
          :readonly="isReadOnly"
          :error="
            fields.income_accounts_id.touched
              ? fields.income_accounts_id.error
              : null
          "
          @blur="fields.income_accounts_id.touched = true"
        />

        <SearchSelect
          v-model="fields.expense_accounts_id.value"
          end-point="finance/accounting/chart-of-accounts"
          label="Expense Account"
          local-storage="accounts"
          :readonly="isReadOnly"
          :error="
            fields.expense_accounts_id.touched
              ? fields.expense_accounts_id.error
              : null
          "
          @blur="fields.expense_accounts_id.touched = true"
        />

        <CheckInput
          v-model="fields.active.value"
          :readonly="isReadOnly"
          label="Active"
          :error="fields.active.touched ? fields.active.error : null"
          @blur="fields.active.touched = true"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
